import { SvgIconProps, TableCell, TableRow, Typography } from "@mui/material";
import { UUID } from "crypto";
import { atom } from "jotai";
import { SearchBase, SearchFilters } from "../../models/search";
import { DynamicDateRange } from "./Filter/DateRangeFilter";

export type ThinkingStepName =
  | "classifying_intent"
  | "intent_classified"
  | "searching_documents"
  | "documents_retrieved"
  | "generating_response"
  | "extracting_transcripts"
  | "transcripts_processed"
  | "preparing_summary"
  | "clustering_documents"
  | "retrieving_records";

export interface ThinkingStep {
  step: string;
  label: string;
  status: "active" | "completed" | "failed";
  timestamp: number;
  detail?: string;
  metadata?: Record<string, unknown> | null;
}
export interface MarketResearchStudy {
  num_respondents: number;
  study_date: string; // "2024-04-23"
  // study_key: string; // "venclexta";
  study_title: string; // "Venclexta Combination Qualitative Research";
  study_format?: "transcript" | "ppt";
  created_ts: string;
  updated_ts: string;
  therapeutic_area: string;
  study_guid: string;
}

export interface StudyQuestion {
  question_id: string;
  question_title: string;
  responses: number;
}

export interface ChatInteraction {
  source_ids: string[];

  search_object?: SearchFilters;
  timestamp?: number;
  user_chat_session: UUID;
  action_id?: UUID;

  user_id: string;
  user_query?: string;

  answer?: string;
  complete?: boolean;
  thinkingSteps?: ThinkingStep[];

  evidence?: Evidence[];
}

export type Evidence = {
  complete_answer: string;
  exhaustive_supporting_evidence: string;
  confidence_score: number;
  detailed_confidence: string;
  question: string;
  comprehensive_notes: string;
  respondent_id?: string;
  retrieved_text_chunk?: string;
  slide_number?: number;
  evidence_type?: string;
  audio_file_id?: string;
  study_title?: string;
};

//{"type":"chat.source","data":{"record_id":"m8KDlpMBn2rDudtZMaTn"}}
export interface PmrDataSource {
  record_id: string; // m8KDlpMBn2rDudtZMaTn
}

export interface PmrChatRequest {
  search_request: SearchBase;
  user_chat_session?: string | null;
  query: string;
}

export type MarketResearchChatStateType = {
  studyIds: string[];
  invocationType?: "user" | "qna";
  noOfResponses?: number;
  question: string; // free text input state variable
  isLoading: boolean;
  socketStatusMessage: string;
  questionId?: string;
  questionResponse?: StudyQuestion | undefined;
  messages: ChatInteraction[];
  showStudySummary?: boolean;
  respondentId?: string;
  base_filters: Record<string, string[] | undefined>;
  // Staged (unapplied) facet selections. Committed to base_filters on Apply.
  draft_base_filters: Record<string, string[] | undefined>;
  evidence?: Evidence[];
  filterCount: number;
  scope: "list" | "chat";
  dateRange?: DynamicDateRange | null;
  // Staged (unapplied) date range. Committed to dateRange on Apply.
  draft_dateRange?: DynamicDateRange | null;
  thinkingSteps: ThinkingStep[];
};

export const MarketResearchStateAtom = atom<MarketResearchChatStateType>({
  studyIds: [],
  invocationType: undefined,
  question: "",
  isLoading: false,
  socketStatusMessage: "",
  messages: [],
  base_filters: {},
  draft_base_filters: {},
  questionId: "",
  questionResponse: undefined,
  showStudySummary: false,
  respondentId: "",
  noOfResponses: undefined,
  filterCount: 0,
  scope: "list",
  dateRange: null,
  draft_dateRange: null,
  thinkingSteps: [],
});

export interface MarketResearchDrawerState {
  open: boolean;
  expanded: string[];
}
export const PMRThinkingStepsCollapsedAtom = atom<boolean>(true);
export const MarketResearchDrawerStateAtom = atom<MarketResearchDrawerState>({
  open: false,
  expanded: [],
});

export const FocusedInteraction = atom<ChatInteraction | undefined>(undefined);

export type TMRTranscripts = {
  respondent_id: number;
  persona: string;
  target_priority?: string;
  specialty: string;
  practice_segment: string;
  heir: string;
  transcript: string;
  transcript_summary: string;
};

export type TStudySummary = {
  summary: string;
};

export type TPMRChat = {
  summary: string;
  question: string;
  evidences: TEvidence[];
};

export type TEvidence = {
  complete_answer: string;
  exhaustive_supporting_evidence: string;
  confidence: number;
  comprehensive_notes: string;
  confidence_details: string;
  evidence: string;
};

export type TQAChatEvidence = {
  evidence: Partial<Evidence>;
  expanded: boolean;

  toggleExpand: (event: React.MouseEvent<HTMLElement>) => void;
};

export interface PMREvidencesProps {
  evidences: Evidence[];
}

export type TAudioTranscript = {
  file: File | null;
  audioId: string;
  respondentIds: string[];
};

export type TAddStudyState = {
  currentStep: number;
  steps: string[];
  isLoading: boolean;
  fromEdit: boolean;

  // Form fields:
  studyId?: UUID;
  studyType: string;
  vendorName: string;
  studyName: string;
  therapeuticArea: string;
  vendorNameArray: string[] | undefined;
  therapeuticAreaArray: string[] | undefined;
  productArray: string[] | undefined;
  editorsArray?: User[] | undefined;
  brand: string;
  startDate: string;
  endDate: string;
  screenerSummaryFile: File | null;
  discussionGuideFile: File | null;
  screenerGuideFile: File | null;
  presentationFile: File | null;
  audioFiles: TAudioTranscript[] | null;

  // We'll compute isNextDisabled dynamically rather than storing it
};

export const AddStudyStateAtom = atom<TAddStudyState>({
  currentStep: 0,
  steps: ["Enter Study Details", "Upload Guides", "Upload Audio"],
  isLoading: false,
  vendorNameArray: undefined,
  therapeuticAreaArray: undefined,
  productArray: undefined,
  editorsArray: undefined,
  fromEdit: false,

  // Form fields:
  studyType: "",
  vendorName: "",
  studyName: "",
  therapeuticArea: "",
  brand: "",
  startDate: "",
  endDate: "",
  screenerSummaryFile: null,
  discussionGuideFile: null,
  screenerGuideFile: null,
  presentationFile: null,
  audioFiles: null,
});

export type TDropdownComponent = {
  array: string[];
  label: string;
  value: string;
  [key: string]: any;
  onChange: (e: any) => void;
};

export type TAddStudyResponse = {
  study_id: UUID;
  vendor_name: string;
  study_name: string;
  therapeutic_area: string;
  product: string;
  study_status: string;
  start_date: string;
  end_date: string;
};

export type TS3UploadResponse = {
  file_id: UUID;
  user_id: string;
  s3_file_url: string;
  s3_filename: string;
  file_name: string;
  file_status: string;
};

export type TStudyListResponse = {
  list_type: string;
  items: string[];
};

export interface TRespondentIdsResponse {
  audio_ids: AudioId[];
}

export interface AudioId {
  audio_file_id: string;
  respondent_ids: string[];
}

export type TTableCellProps = React.ComponentProps<typeof TableCell> & {
  width?: string;
  fontSize?: string;
  children?: React.ReactNode;
};

export type TTableRowProps = React.ComponentProps<typeof TableRow> & {
  width?: string;
  children: React.ReactNode;
};

export type TEditAudioFileUploadProps = {
  id: number;
  study_name: string;
  uploaded_date: string;
  uploaded_by: string;
  status: string;
  audio_id: string;
  respondent_ids: string[];
};

export type TMenuButtonType = {
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  iconProps?: SvgIconProps;
  labelProps?: React.ComponentProps<typeof Typography>;
  onClick?: () => void;
};

export type TButtonGroupType = {
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  handleOnClick: (e) => void;
};

export type TManageStudiesResponse = {
  study_id: UUID;
  vendor_name: string;
  study_name: string;
  therapeutic_area: string;
  product: string;
  study_status: "CREATED" | "GATHERING_INPUTS" | "ALL_AUDIO_TRANSCRIBED" | "PROCESSING" | "PUBLISHED";
  start_date: string;
  end_date: string;

  created_ts: string; // "2025-03-23T21:49:06.266983";
  updated_ts: string; // "2025-03-31T21:44:26.366426";
  editors?: string[];
  owning_user_id: UUID;
  owning_user_auth_id: "leibniz3";
  study_type: string;
};

export type TManageStudiesStore = {
  page: number;
  byEditors: boolean;
  studies: TManageStudiesResponse[];
};

export const TManageStudiesStore = atom<TManageStudiesStore>({
  page: 1,
  byEditors: false,
  studies: [],
});

export interface ManageStudiesState {
  studyCounts: Record<string, number>;
  isAPILoading: boolean;
  // studyId?: string; // This does not belong here
  openModal: boolean;
  isDeleteLoading: boolean;
  audioFile: StudyFile[];
}

export type TCompleteSaveButtonType = {
  anchorEl: null | HTMLElement;
  open: boolean;
  isSaveDisabled: boolean;
  isCompleteDisabled: boolean;
  // handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  handleSaveOnlyClick: () => void;
  handleCompleteClick: () => void;
};

export type TVerifyStudySummaryFileResponse = {
  success: boolean;
  error_message: string;
};

// -- PMR Study Models --------------------------------------------------------
export type Study = {
  study_guid: UUID; // "f1f24bbd-538a-41d1-816c-05107ed42f63";
  study_name: string; // "Davi hears a Who";

  vendor: Vendor;
  therapeutic_area: TherapeuticArea;
  product: Product;

  study_status: string; // "PUBLISHED";
  study_type: string; // "Qualitative";
  study_start_date?: string; // "2025-04-09T00:00:00";
  study_end_date?: string;
  study_summary?: string;

  editors: StudyEditor[];
  interviews: Interview[];
  respondents: Respondent[];
  files: StudyFile[];

  owner: User;
  created_ts: string; // "2025-04-15T16:33:44.458785";
  updated_ts: string; // "2025-04-15T16:37:08.279007";
};

export type User = {
  user_guid: UUID; // "29b247e0-5eff-4934-80f4-1e507834535b";
  auth_user_id: string; // "leibniz3";
  auth_user_email: string; // "leibniz3@gene.com";
  display_name: string;
};

export type StudyEditor = {
  user: User;
};

export type Vendor = {
  vendor_guid: UUID; // "a15d4494-e524-4193-a95f-a6a04a52dc00";
  vendor_name: string; // "Test Vendor 1";
};

export type TherapeuticArea = {
  therapeutic_area_guid: UUID; // "d3c7e497-0158-4f13-977d-8868f46f60d4";
  therapeutic_area_name: string; // "TA 1";
};

export type Product = {
  product_guid: UUID; // "b80b5ba0-e276-4295-ba48-9a9e3214f2c4";
  product_name: string; // "Product 1";
};

export type Interview = {
  interview_guid: UUID;
  discussion_guide_file_guid?: UUID;
  audio_file_guid?: UUID;
  transcript_file_guid?: UUID;
  extra_transcript_details?: Record<string, unknown>;
  transcript?: string;
  transcript_summary?: string;

  transcript_file: StudyFile;
  audio_file: StudyFile;
  respondents: Respondent[];

  processes: Process[];
};

export type FileTypes =
  | "DISCUSSION_GUIDE"
  | "AUDIO"
  | "TRANSCRIPT"
  | "STUDY_SUMMARY"
  | "SCREENER_GUIDE"
  | "SCREENER_SUMMARY"
  | "POWERPOINT";

export enum ProcessType {
  TRANSCRIBE_AUDIO_FILE = "TRANSCRIBE_AUDIO_FILE",
  EXTRACT_RESPONDENT_METADATA = "EXTRACT_RESPONDENT_METADATA",
  GENERATE_TRANSCRIPT_SUMMARY = "GENERATE_TRANSCRIPT_SUMMARY",
  GENERATE_STUDY_SUMMARY = "GENERATE_STUDY_SUMMARY",
  UPLOAD_FILE = "UPLOAD_FILE",
  GENERATE_QA_PAIRS = "GENERATE_QA_PAIRS",
}

export enum ProcessStatus {
  STARTED = "STARTED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export type StudyFile = {
  file_guid: UUID; //"69fc7514-010b-468f-bf40-020dab691b93",
  file_type: FileTypes | string;
  uploaded_file: UploadedFile | null; // null | { uploaded_file_guid: UUID; s3_file_location: string; uploaded_timestamp: string; }

  owning_user_guid: UUID; // "29b247e0-5eff-4934-80f4-1e507834535b",
  owner: User;
  file_metadata: Record<string, unknown>; // { audio_file_id: "6j", },
};

export type UploadedFile = {
  uploaded_file_guid: UUID; // "5f135c77-303f-4255-856f-a0b4118364d9";
  s3_file_location: string; //"s3://cmg-oasis-dev-deepsense-pmr/pmr_studies/f1f24bbd-538a-41d1-816c-05107ed42f63/audio/processed/Atlas Primary_Xolair Food Allergy_ID01_20240808_speakers.json";
  uploaded_timestamp: string; // "2025-04-10T01:55:01.188386";
  original_name?: string;
};

export type Respondent = {
  respondent_guid: UUID;
  respondent_id: string;
  persona?: string;
  target_priority?: string;
  specialty?: string;
  practice_segment?: string;
  heir?: string;
  extra_respondent_details?: Record<string, unknown>;
};

export type Process = {
  process_guid: UUID;
  process_type: ProcessType;
  process_status: ProcessStatus;

  created_at: string; // Datetime
  updated_at: string; // Datetime

  process_details?: Record<string, unknown>;
};

export type TStudyIconProps = {
  therapeuticArea: string;
  size?: number;
  fallbackSrc?: string;
};
